import { memo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { 
  FileText, 
  CheckCircle, 
  Clock, 
  Edit,
  Trash2,
  UserX,
  UserCheck,
  MapPin,
  Plus,
  MoreHorizontal,
  Phone,
  Mail,
  Building2,
  Loader2,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { useTranslation } from 'react-i18next';
import { api } from "../App";

// Map designation to readable label
const getDesignationLabel = (designation) => {
  const labels = {
    tahsildar: "Tahsildar",
    deputy_tahsildar: "Deputy Tahsildar",
    shirestedar: "Shirestedar",
    revenue_inspector: "Revenue Inspector",
    village_accountant: "Village Accountant",
    first_division_clerk: "First Division Clerk",
    second_division_clerk: "Second Division Clerk",
    ddlr: "DDLR",
    adlr: "ADLR",
    surveyor: "Surveyor",
  };
  return labels[designation] || designation;
};

// Format circle name for display
const formatCircleName = (circle) => {
  if (!circle) return null;
  return circle.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

// Get circle assignments in normalized format
const getCircleAssignments = (official) => {
  if (official.circle_assignments && official.circle_assignments.length > 0) {
    return official.circle_assignments;
  }
  if (official.assigned_circle) {
    return [{
      hobli: official.assigned_hobli,
      circle: official.assigned_circle
    }];
  }
  return [];
};

// Individual Action Modal
function OfficialActionModal({ 
  official, 
  isOpen, 
  onClose, 
  onEdit, 
  onToggleStatus, 
  onDelete,
  onAssignCircle,
  onRemoveCircle,
  userDesignation 
}) {
  const { t } = useTranslation();
  const [villagesByCircle, setVillagesByCircle] = useState({});
  const [loadingVillages, setLoadingVillages] = useState(false);
  const [expandedCircles, setExpandedCircles] = useState({});
  
  const isDisabled = official?.is_disabled;
  const isVA = official?.designation === "village_accountant";
  const isTahsildar = userDesignation === "tahsildar";
  const circleAssignments = official ? getCircleAssignments(official) : [];
  const hasAssignments = circleAssignments.length > 0;

  // Fetch villages for circle assignments when modal opens for a VA
  useEffect(() => {
    if (!isOpen || !official || !isVA || !hasAssignments) {
      return;
    }
    
    const fetchVillagesForCircles = async () => {
      setLoadingVillages(true);
      const villagesMap = {};
      
      try {
        for (const assignment of circleAssignments) {
          const key = `${assignment.hobli}-${assignment.circle}`;
          try {
            // Use the correct API endpoint: /circle-villages/villages/{circle}
            const response = await api.get(`/circle-villages/villages/${assignment.circle}`);
            if (response.data?.villages) {
              villagesMap[key] = response.data.villages;
            }
          } catch (err) {
            console.error(`Error fetching villages for ${key}:`, err);
            villagesMap[key] = [];
          }
        }
        setVillagesByCircle(villagesMap);
      } catch (error) {
        console.error("Error fetching villages:", error);
      } finally {
        setLoadingVillages(false);
      }
    };
    
    fetchVillagesForCircles();
  }, [isOpen, official?.id, isVA, hasAssignments]);

  const toggleCircleExpand = (key) => {
    setExpandedCircles(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  if (!official) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {official.name}
            {isDisabled && (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                {t('officialsManagement.disabled')}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-left">
            <Badge 
              className={`text-xs ${
                official.department === "revenue" 
                  ? "bg-emerald-100 text-emerald-800 border-emerald-200" 
                  : "bg-blue-100 text-blue-800 border-blue-200"
              }`}
            >
              {t(`designation.${official.designation}`) || getDesignationLabel(official.designation)}
            </Badge>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Contact Info */}
          <div className="flex flex-col gap-2 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-400" />
              {official.email}
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-400" />
              {official.phone || "-"}
            </div>
          </div>

          {/* Workload Stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center p-3 bg-stone-50 rounded-lg">
              <div className="flex items-center justify-center gap-1 text-slate-600">
                <FileText className="w-4 h-4" />
                <span className="font-bold text-lg">{official.workload?.total_assigned || 0}</span>
              </div>
              <p className="text-xs text-slate-500">{t('officialsManagement.totalAssigned')}</p>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center justify-center gap-1 text-yellow-600">
                <Clock className="w-4 h-4" />
                <span className="font-bold text-lg">{official.workload?.pending || 0}</span>
              </div>
              <p className="text-xs text-slate-500">{t('officialsManagement.pending')}</p>
            </div>
            <div className="text-center p-3 bg-emerald-50 rounded-lg">
              <div className="flex items-center justify-center gap-1 text-emerald-600">
                <CheckCircle className="w-4 h-4" />
                <span className="font-bold text-lg">{official.workload?.completed || 0}</span>
              </div>
              <p className="text-xs text-slate-500">{t('officialsManagement.completed')}</p>
            </div>
          </div>

          {/* Circle Assignments for Village Accountants */}
          {isVA && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">
                    {t('officialsManagement.assignedCircles') || 'Assigned Circles'}
                    {hasAssignments && <span className="ml-1">({circleAssignments.length})</span>}
                  </span>
                </div>
                {isTahsildar && onAssignCircle && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onClose();
                      onAssignCircle(official);
                    }}
                    className="text-amber-700 hover:text-amber-800 hover:bg-amber-100 border-amber-300 h-7 px-2"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    {t('officialsManagement.addCircle') || 'Add'}
                  </Button>
                )}
              </div>
              
              {hasAssignments ? (
                <div className="space-y-2">
                  {loadingVillages && (
                    <div className="flex items-center gap-2 text-sm text-amber-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('common.loading', { name: localStorage.getItem('userName') || '' }) || 'Please wait...'}
                    </div>
                  )}
                  {circleAssignments.map((assignment, index) => {
                    const circleKey = `${assignment.hobli}-${assignment.circle}`;
                    const villages = villagesByCircle[circleKey] || [];
                    const isExpanded = expandedCircles[circleKey];
                    
                    return (
                      <div 
                        key={circleKey}
                        className="bg-white rounded border border-amber-100 overflow-hidden"
                      >
                        {/* Circle Header - Clickable to expand/collapse */}
                        <div 
                          className="flex items-center justify-between px-2 py-1.5 bg-amber-50/50 cursor-pointer hover:bg-amber-100/50 transition-colors"
                          onClick={() => toggleCircleExpand(circleKey)}
                          data-testid={`circle-expand-${assignment.circle}`}
                        >
                          <div className="flex items-center gap-2 flex-1">
                            <span className="flex items-center">
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-amber-500" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-amber-500" />
                              )}
                            </span>
                            <div className="text-sm">
                              <span className="text-amber-700 font-medium">{t('officialsManagement.circleLabel') || 'Circle'}:</span>
                              <span className="font-medium text-amber-900 ml-1">
                                {formatCircleName(assignment.circle)}
                              </span>
                              <span className="text-amber-700 font-medium ml-2">{t('officialsManagement.hobliLabel') || 'Hobli'}:</span>
                              <span className="text-amber-600 ml-1">
                                {assignment.hobli}
                              </span>
                              {villages.length > 0 && (
                                <span className="text-xs text-blue-600 ml-2">
                                  ({villages.length} {t('officialsManagement.villages') || 'villages'})
                                </span>
                              )}
                            </div>
                          </div>
                          {isTahsildar && onRemoveCircle && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent triggering expand
                                onClose();
                                onRemoveCircle(official, assignment);
                              }}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        
                        {/* Villages List - Expandable */}
                        {isExpanded && villages.length > 0 && (
                          <div className="px-3 py-2 border-t border-amber-100 bg-white">
                            <p className="text-xs font-medium text-slate-600 mb-1.5">
                              {t('officialsManagement.assignedVillages') || 'Assigned Villages'}:
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {villages.map((village, vIdx) => {
                                // Handle both string and object village formats
                                const villageName = typeof village === 'string' 
                                  ? village 
                                  : (village.name_kn || village.name_en || village.name || 'Unknown');
                                const villageNameEn = typeof village === 'object' ? village.name_en : null;
                                const isHQ = typeof village === 'object' && village.is_hq;
                                
                                return (
                                  <span 
                                    key={vIdx}
                                    className={`text-xs px-2 py-0.5 rounded border ${isHQ ? 'bg-blue-50 text-blue-700 border-blue-200 font-medium' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}
                                    title={villageNameEn ? `${villageNameEn}${isHQ ? ' (HQ)' : ''}` : undefined}
                                  >
                                    {villageName}
                                    {isHQ && <span className="ml-1 text-blue-500">★</span>}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {isExpanded && villages.length === 0 && !loadingVillages && (
                          <div className="px-3 py-2 border-t border-amber-100 bg-white">
                            <p className="text-xs text-slate-400 italic">
                              {t('officialsManagement.noVillagesAssigned') || 'No villages data available'}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="text-sm text-amber-600 italic">
                  {t('officialsManagement.noCircleAssigned') || 'Not assigned'}
                </span>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-2 pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => {
                onClose();
                onEdit(official);
              }}
              className="w-full justify-start text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <Edit className="w-4 h-4 mr-2" />
              {t('officialsManagement.edit')}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                onClose();
                onToggleStatus(official);
              }}
              className={`w-full justify-start ${isDisabled ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50' : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'}`}
            >
              {isDisabled ? (
                <>
                  <UserCheck className="w-4 h-4 mr-2" />
                  {t('officialsManagement.enable')}
                </>
              ) : (
                <>
                  <UserX className="w-4 h-4 mr-2" />
                  {t('officialsManagement.disable')}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                onClose();
                onDelete(official);
              }}
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t('officialsManagement.delete')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const OfficialsTable = memo(function OfficialsTable({ 
  officials, 
  onEdit, 
  onToggleStatus, 
  onDelete,
  onAssignCircle,
  onRemoveCircle,
  userDesignation,
  department
}) {
  const { t } = useTranslation();
  const [selectedOfficial, setSelectedOfficial] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);

  const handleRowClick = (official) => {
    setSelectedOfficial(official);
    setShowActionModal(true);
  };

  const closeModal = () => {
    setShowActionModal(false);
    setSelectedOfficial(null);
  };

  if (!officials || officials.length === 0) {
    return null;
  }

  const departmentColor = department === "revenue" ? "emerald" : "blue";

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white shadow-sm">
        <table className="w-full text-sm" data-testid={`officials-table-${department}`}>
          <thead className={`bg-${departmentColor}-50 border-b border-${departmentColor}-200`}>
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-slate-700">
                {t('officialsManagement.name') || 'Name'}
              </th>
              <th className="text-left px-4 py-3 font-semibold text-slate-700 hidden sm:table-cell">
                {t('officialsManagement.designation') || 'Designation'}
              </th>
              <th className="text-left px-4 py-3 font-semibold text-slate-700 hidden md:table-cell">
                {t('officialsManagement.contact') || 'Contact'}
              </th>
              <th className="text-center px-4 py-3 font-semibold text-slate-700">
                {t('officialsManagement.workload') || 'Workload'}
              </th>
              <th className="text-left px-4 py-3 font-semibold text-slate-700 hidden lg:table-cell">
                {t('officialsManagement.status') || 'Status'}
              </th>
              <th className="text-center px-4 py-3 font-semibold text-slate-700 w-20">
                {t('officialsManagement.actions') || 'Actions'}
              </th>
            </tr>
          </thead>
          <tbody>
            {officials.map((official, index) => {
              const isDisabled = official.is_disabled;
              const circleAssignments = getCircleAssignments(official);
              const isVA = official.designation === "village_accountant";
              
              return (
                <tr 
                  key={official.id}
                  className={`border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors ${isDisabled ? 'opacity-60 bg-stone-50' : ''}`}
                  onClick={() => handleRowClick(official)}
                  data-testid={`official-row-${official.id}`}
                >
                  {/* Name Column */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="font-medium text-slate-900 flex items-center gap-2">
                          {official.name}
                          {isDisabled && (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                              {t('officialsManagement.disabled')}
                            </Badge>
                          )}
                        </div>
                        {/* Show designation on mobile */}
                        <div className="text-xs text-slate-500 sm:hidden">
                          {t(`designation.${official.designation}`) || getDesignationLabel(official.designation)}
                        </div>
                      </div>
                    </div>
                  </td>
                  
                  {/* Designation Column */}
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <Badge 
                      variant="outline"
                      className={`text-xs ${
                        official.department === "revenue" 
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                          : "bg-blue-50 text-blue-700 border-blue-200"
                      }`}
                    >
                      {t(`designation.${official.designation}`) || getDesignationLabel(official.designation)}
                    </Badge>
                    {/* Show hobli assignment for Revenue Inspectors */}
                    {official.designation === "revenue_inspector" && official.assigned_hobli && (
                      <div className="flex flex-col gap-1 mt-1">
                        <span className="text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                          <span className="font-medium">{t('officialsManagement.hobliLabel') || 'Hobli'}:</span> {official.assigned_hobli}
                        </span>
                      </div>
                    )}
                    {/* Show circle assignments for VAs with Circle and Hobli labels */}
                    {isVA && circleAssignments.length > 0 && (
                      <div className="flex flex-col gap-1 mt-1">
                        {circleAssignments.slice(0, 2).map((a, i) => (
                          <span key={i} className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                            <span className="font-medium">{t('officialsManagement.circleLabel') || 'Circle'}:</span> {formatCircleName(a.circle)}, <span className="font-medium">{t('officialsManagement.hobliLabel') || 'Hobli'}:</span> {a.hobli}
                          </span>
                        ))}
                        {circleAssignments.length > 2 && (
                          <span className="text-xs text-amber-600">+{circleAssignments.length - 2} {t('officialsManagement.moreCircles') || 'more'}</span>
                        )}
                      </div>
                    )}
                  </td>
                  
                  {/* Contact Column */}
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="text-xs text-slate-600">
                      <div className="truncate max-w-[150px]">{official.email}</div>
                      <div className="text-slate-400">{official.phone || "-"}</div>
                    </div>
                  </td>
                  
                  {/* Workload Column */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-3">
                      <div className="text-center" title={t('officialsManagement.pending')}>
                        <span className="inline-flex items-center gap-1 text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded text-xs font-medium">
                          <Clock className="w-3 h-3" />
                          {official.workload?.pending || 0}
                        </span>
                      </div>
                      <div className="text-center" title={t('officialsManagement.completed')}>
                        <span className="inline-flex items-center gap-1 text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded text-xs font-medium">
                          <CheckCircle className="w-3 h-3" />
                          {official.workload?.completed || 0}
                        </span>
                      </div>
                    </div>
                  </td>
                  
                  {/* Status Column */}
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <Badge 
                      variant="outline"
                      className={isDisabled 
                        ? "bg-red-50 text-red-700 border-red-200" 
                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                      }
                    >
                      {isDisabled ? t('officialsManagement.inactive') || 'Inactive' : t('officialsManagement.active') || 'Active'}
                    </Badge>
                  </td>
                  
                  {/* Actions Column */}
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRowClick(official)}
                      className="h-8 w-8 p-0 hover:bg-slate-100"
                      data-testid={`official-actions-${official.id}`}
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Action Modal */}
      <OfficialActionModal
        official={selectedOfficial}
        isOpen={showActionModal}
        onClose={closeModal}
        onEdit={onEdit}
        onToggleStatus={onToggleStatus}
        onDelete={onDelete}
        onAssignCircle={onAssignCircle}
        onRemoveCircle={onRemoveCircle}
        userDesignation={userDesignation}
      />
    </>
  );
});

export default OfficialsTable;
